import { c as _c } from "react/compiler-runtime";
// `<JsonLdScript>` — renders a `<script type="application/ld+json">` tag
// containing one or more Schema.org nodes. Use this in any page that
// owns a shareable URL so Google, Pinterest, LinkedIn, Schema.org
// validators, and AI crawlers (Bing, ChatGPT browsing) can read
// structured data.
//
// Implementation note: `dangerouslySetInnerHTML` is used instead of JSX
// text children for React 19 / Next.js 16 compatibility. React 19 emits
// a warning ("Encountered a script tag while rendering React component")
// when a `<script>` with text children is encountered during hydration
// inside a client component tree — even when the script itself is
// server-rendered. `dangerouslySetInnerHTML` sidesteps this by setting
// the script content via the DOM property rather than React's reconciler.
//
// The project-wide ban on `dangerouslySetInnerHTML` covers user-supplied
// content (XSS risk). JSON-LD payloads are not user content — they are
// structured data built from validated API responses. The payload is
// additionally escaped via `safeJsonLdStringify` which replaces `<` with
// `<`, preventing `</script>` injection. This usage is explicitly
// documented as safe.

import type { JSX } from 'react';
import { safeJsonLdStringify } from './escape';
import type { JsonLdPayload } from './types';
export interface JsonLdScriptProps {
  data: JsonLdPayload;
  /** Optional id for debugging / dedup if multiple JSON-LD blocks render
   *  on a single page. Surfaces with multiple payloads pass an array to
   *  a single `<JsonLdScript>` instead — the spec accepts an array of
   *  nodes in one script tag. */
  id?: string;
}

/**
 * Renders a JSON-LD script tag. Returns `null` when `data` is `null` so
 * surfaces without structured data emit nothing.
 *
 * Must render inside a Server Component (page.tsx, layout.tsx) so the
 * script appears in the initial HTML crawlers fetch, NOT after hydration.
 */
export function JsonLdScript(t0) {
  const $ = _c(9);
  const {
    data,
    id
  } = t0;
  if (data === null) {
    return null;
  }
  const payload = Array.isArray(data) && data.length === 1 ? data[0] : data;
  if (payload === null || payload === undefined) {
    return null;
  }
  let t1;
  if ($[0] !== payload) {
    t1 = safeJsonLdStringify(payload);
    $[0] = payload;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const json = t1;
  let t2;
  if ($[2] !== id) {
    t2 = id !== undefined ? {
      id
    } : {};
    $[2] = id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== json) {
    t3 = {
      __html: json
    };
    $[4] = json;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <script type="application/ld+json" {...t2} dangerouslySetInnerHTML={t3} />;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}